import type { AuthError } from '@supabase/supabase-js';

import { PASSWORD_POLICY_DESCRIPTION } from '@rollops/app-shared/utils/passwordPolicy';

/**
 * Maps Supabase auth errors to user-friendly messages
 * Returns generic messages that don't reveal specific authentication issues
 */
export function getAuthErrorMessage(error: AuthError | null | undefined): string {
  if (!error) {
    return 'An unexpected error occurred. Please try again.';
  }

  // Log the actual error for debugging (but don't show it to users)
  console.error('[Auth] Authentication error:', {
    status: error.status,
    code: error.code || 'unknown',
    message: error.message,
    name: error.name,
  });

  // Map common Supabase auth error codes to user-friendly messages
  const errorCode = error.code || '';
  const errorMessage = error.message?.toLowerCase() || '';

  // Weak password (Supabase password strength requirements not met).
  // Checked before the invalid-credentials branch because GoTrue returns
  // weak_password with HTTP 400, which the broader 400-status check below
  // would otherwise swallow.
  if (
    errorCode === 'weak_password' ||
    errorMessage.includes('weak_password') ||
    errorMessage.includes('password should contain') ||
    errorMessage.includes('password must contain') ||
    errorMessage.includes('alphanumeric') ||
    errorMessage.includes('alpha-numeric')
  ) {
    return PASSWORD_POLICY_DESCRIPTION;
  }

  // Email not confirmed. GoTrue returns this as HTTP 400 too, so it has to be
  // checked BEFORE the status-based credential branch below — behind it, a
  // brand-new account that had not clicked its confirmation link read as
  // "Invalid email or password" with the right password (2026-10-06), and
  // nothing told the user what to actually do.
  if (
    errorCode === 'email_not_confirmed' ||
    errorMessage.includes('email not confirmed') ||
    errorMessage.includes('email address not confirmed')
  ) {
    return 'Please verify your email address before signing in. Check your inbox for a confirmation link.';
  }

  // Invalid credentials (wrong email/password)
  if (
    errorCode === 'invalid_credentials' ||
    errorCode === 'invalid_grant' ||
    errorMessage.includes('invalid login credentials') ||
    errorMessage.includes('invalid email or password') ||
    errorMessage.includes('email not found') ||
    errorMessage.includes('incorrect password') ||
    error.status === 400
  ) {
    return 'Invalid email or password. Please check your credentials and try again.';
  }

  // Too many requests / rate limiting
  if (
    errorCode === 'too_many_requests' ||
    errorMessage.includes('too many requests') ||
    errorMessage.includes('rate limit') ||
    error.status === 429
  ) {
    return 'Too many login attempts. Please wait a few minutes and try again.';
  }

  // Network/connection errors
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('connection') ||
    error.status === 0
  ) {
    return 'Unable to connect. Please check your internet connection and try again.';
  }

  // Past this point the failure did not come from the credential check — a
  // 5xx from GoTrue, a 403 from the edge, a response the client could not
  // parse. Guessing "wrong password" sent a user back to re-paste a password
  // that was already right; say what kind of failure it was and keep the one
  // fact we have, the status, in the message so a report carries it.
  // Neutral wording on purpose: this mapper also serves reset-password.
  const tag = referenceTag(error);
  if (error.status !== undefined && error.status >= 500) {
    return `Something went wrong on our end${tag}. Please try again in a moment.`;
  }
  return `The request failed${tag}. Please try again.`;
}

/**
 * The one fact a user can read back to us about a failure we did not
 * recognise: the HTTP status when there is one, else the code. Rendered as a
 * parenthetical so it drops into any sentence.
 */
function referenceTag(error: AuthError): string {
  if (error.status) return ` (error ${error.status})`;
  if (error.code) return ` (${error.code})`;
  return '';
}



export function getSignupErrorMessage(error: AuthError | null | undefined): string {
  if (!error) {
    return 'An unexpected error occurred. Please try again.';
  }

  console.error('[Auth] Signup error:', {
    status: error.status,
    code: error.code || 'unknown',
    message: error.message,
    name: error.name,
  });

  const errorCode = error.code || '';
  const errorMessage = error.message?.toLowerCase() || '';

  // Weak password
  if (
    errorCode === 'weak_password' ||
    errorMessage.includes('weak_password') ||
    errorMessage.includes('password should contain') ||
    errorMessage.includes('password must contain') ||
    errorMessage.includes('alphanumeric') ||
    errorMessage.includes('alpha-numeric')
  ) {
    return PASSWORD_POLICY_DESCRIPTION;
  }

  // Email already registered. Use a non-committal message so we don't
  // confirm whether an email is registered (prevents account enumeration);
  // legitimate users can still recover via sign-in or password reset.
  if (
    errorCode === 'email_exists' ||
    errorCode === 'user_already_exists' ||
    errorMessage.includes('already registered') ||
    errorMessage.includes('already exists') ||
    errorMessage.includes('email already')
  ) {
    return 'We couldn’t create your account. Please try signing in or resetting your password, or use a different email.';
  }

  // Invalid email format
  if (
    errorCode === 'invalid_email' ||
    errorMessage.includes('invalid email') ||
    errorMessage.includes('unable to validate email')
  ) {
    return 'Please enter a valid email address.';
  }

  // Too many requests
  if (
    errorCode === 'too_many_requests' ||
    errorMessage.includes('too many requests') ||
    errorMessage.includes('rate limit') ||
    error.status === 429
  ) {
    return 'Too many attempts. Please wait a few minutes and try again.';
  }

  // Network errors
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('connection') ||
    error.status === 0
  ) {
    return 'Unable to connect. Please check your internet connection and try again.';
  }

  const tag = referenceTag(error);
  if (error.status !== undefined && error.status >= 500) {
    return `Something went wrong on our end${tag}. Please try again in a moment.`;
  }
  return `Account creation failed${tag}. Please try again.`;
}






